import { ChevronLeftIcon } from '@approvalmax/ui';
import { Box, Button, Grid, Popup, Text, toast } from '@approvalmax/ui/src/components';
import { dateTimeHelpers, intl } from '@approvalmax/utils';
import { useSetAtom } from 'jotai';
import moment from 'moment';
import { FC, useCallback, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useUnmount } from 'react-use';
import { t } from 'services/localization';
import { noValue } from 'shared/constants';
import { CreateConversionQuote, useCreateConversion, useCreateConversionQuote } from 'shared/data/wallets/v1';
import { useCompanyIdBasedOnUrl, useTimer } from 'shared/hooks';

import { convertCurrencyPopupState } from '../../ConvertCurrencyPopup.states';
import { ConfirmConvertCurrencyPopupContentProps } from './ConfirmConvertCurrencyPopupContent.types';

export const ConfirmConvertCurrencyPopupContent: FC<ConfirmConvertCurrencyPopupContentProps> = (props) => {
    const { fromCurrency, toCurrency, fixedSide, amount, onSuccess, onBack } = props;

    const { companyId: companyIdFromUrl } = useCompanyIdBasedOnUrl();
    const companyId = props.companyId ?? companyIdFromUrl;
    const setConvertCurrencyPopup = useSetAtom(convertCurrencyPopupState);
    const [quoteDate, setQuoteDate] = useState<string | null>(null);
    // this state prevents text flickering on recreating quote
    const [conversionQuote, setConversionQuote] = useState<CreateConversionQuote['response']>();
    // this state prevents text flickering on recreating quote
    const [isLoadedOnce, setIsLoadedOnce] = useState(false);

    const {
        mutate: createConversionQuote,
        isLoading: isLoadingCreateConversionQuote,
        isError: isErrorCreateConversionQuote,
        isSuccess: isSuccessCreateConversionQuote,
    } = useCreateConversionQuote({
        onSuccess: (data) => {
            setConversionQuote(data);
            setQuoteDate(moment().format('DD MMM YYYY HH:mm'));
        },
    });

    const { mutate: createConversion, isLoading: isLoadingCreateConversion } = useCreateConversion({
        onSuccess: () => {
            setConvertCurrencyPopup({
                isOpen: false,
            });
            onSuccess();

            toast.success(t('conversion_successfully_es_will_be_updated_soon'));
        },
    });

    const { startTimer, timer, stopTimer } = useTimer();

    const handleCreateConversionQuote = useCallback(() => {
        createConversionQuote({
            params: {
                path: {
                    organizationId: companyId,
                },
            },
            body: {
                buyCurrency: toCurrency,
                sellCurrency: fromCurrency,
                amount,
                fixedSide,
            },
        });
    }, [toCurrency, fromCurrency, amount, fixedSide, companyId, createConversionQuote]);

    useEffect(() => {
        if (isSuccessCreateConversionQuote) {
            setIsLoadedOnce(true);
        }
    }, [isSuccessCreateConversionQuote]);

    useEffect(() => {
        if (isErrorCreateConversionQuote || isLoadingCreateConversionQuote) {
            return;
        }

        if (!conversionQuote) {
            handleCreateConversionQuote();
        } else {
            startTimer({
                seconds: 30,
                onFinish: () => {
                    handleCreateConversionQuote();
                },
            });
        }
    }, [
        conversionQuote,
        handleCreateConversionQuote,
        isErrorCreateConversionQuote,
        isLoadingCreateConversionQuote,
        startTimer,
    ]);

    useUnmount(() => {
        stopTimer();
    });

    const handleBack = useCallback(() => {
        setConvertCurrencyPopup({
            isOpen: true,
            fromDefaultCurrency: fromCurrency,
            toDefaultCurrency: toCurrency,
        });

        onBack();
    }, [setConvertCurrencyPopup, fromCurrency, toCurrency, onBack]);

    const handleSubmit = useCallback(() => {
        if (!conversionQuote) {
            return;
        }

        const now = Date.now();

        const { id: quoteId, ...restQuote } = conversionQuote;

        createConversion({
            params: {
                path: {
                    organizationId: companyId,
                },
            },
            body: {
                idempotencyId: `${quoteId}-${now}`,
                conversionQuoteId: quoteId,
                isApproved: true,
                ...restQuote,
            },
        });
    }, [createConversion, conversionQuote, companyId]);

    const isLoading =
        (isLoadingCreateConversionQuote || !conversionQuote) && !isErrorCreateConversionQuote && !isLoadedOnce;

    return (
        <>
            <Popup.Header title={t('review')} />

            <Popup.Body>
                <Grid rowGap={16}>
                    <Text color='midnight80' font='body' fontSize='medium'>
                        {t('please_check_the_data_and_confirm_the_conversion')}
                    </Text>

                    <Grid gridTemplateColumns='1fr 1fr' gap={12}>
                        <Box color='midnight20' radius='small' spacing='12'>
                            <Grid rowGap={8}>
                                <Text font='label' fontSize='medium'>
                                    {t('from_1')}
                                </Text>

                                <Grid gridTemplateColumns='max-content max-content' gap={8}>
                                    <Text color='midnight70' font='label' fontSize='medium'>
                                        {t('wallet')}
                                    </Text>

                                    <Text font='label' fontSize='medium'>
                                        {isLoading ? (
                                            <Skeleton inline width={70} />
                                        ) : (
                                            conversionQuote?.sellCurrency ?? noValue
                                        )}
                                    </Text>
                                </Grid>

                                <Grid gridTemplateColumns='max-content max-content' gap={8}>
                                    <Text color='midnight70' font='label' fontSize='medium'>
                                        {t('sell')}
                                    </Text>

                                    <Text font='label' fontSize='medium'>
                                        {isLoading ? (
                                            <Skeleton inline width={70} />
                                        ) : conversionQuote ? (
                                            intl.formatCurrency(
                                                conversionQuote.clientSellAmount,
                                                conversionQuote.sellCurrency
                                            )
                                        ) : (
                                            noValue
                                        )}
                                    </Text>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box color='midnight20' radius='small' spacing='12'>
                            <Grid rowGap={8}>
                                <Text font='label' fontSize='medium'>
                                    {t('to_1')}
                                </Text>

                                <Grid gridTemplateColumns='max-content max-content' gap={8}>
                                    <Text color='midnight70' font='label' fontSize='medium'>
                                        {t('wallet')}
                                    </Text>

                                    <Text font='label' fontSize='medium'>
                                        {isLoading ? (
                                            <Skeleton inline width={70} />
                                        ) : (
                                            conversionQuote?.buyCurrency ?? noValue
                                        )}
                                    </Text>
                                </Grid>

                                <Grid gridTemplateColumns='max-content max-content' gap={8}>
                                    <Text color='midnight70' font='label' fontSize='medium'>
                                        {t('buy')}
                                    </Text>

                                    <Text font='label' fontSize='medium'>
                                        {isLoading ? (
                                            <Skeleton inline width={70} />
                                        ) : conversionQuote ? (
                                            intl.formatCurrency(
                                                conversionQuote.clientBuyAmount,
                                                conversionQuote.buyCurrency
                                            )
                                        ) : (
                                            noValue
                                        )}
                                    </Text>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    <Grid rowGap={8}>
                        <Grid gridTemplateColumns='max-content max-content' gap={8}>
                            <Text color='midnight70' font='label' fontSize='medium'>
                                {t('exchange_rate')}
                            </Text>

                            <Text font='label' fontSize='medium'>
                                {isLoading ? (
                                    <Skeleton inline width={70} />
                                ) : conversionQuote ? (
                                    t('fromcurrency_rate_tocurrency_date', {
                                        fromCurrency: conversionQuote.sellCurrency,
                                        toCurrency: conversionQuote.buyCurrency,
                                        rate: intl.formatNumber(conversionQuote.clientRate, 'inherit'),
                                        date: quoteDate,
                                    })
                                ) : null}
                            </Text>
                        </Grid>

                        <Grid gridTemplateColumns='max-content max-content' gap={8}>
                            <Text color='midnight70' font='label' fontSize='medium'>
                                {t('date_1')}
                            </Text>

                            <Grid gridTemplateColumns='max-content max-content' gap={16}>
                                <Text font='label' fontSize='medium'>
                                    {conversionQuote?.possibleConversionDate || <Skeleton inline width={50} />}
                                </Text>

                                <Text color='midnight70' font='label' fontSize='medium' whiteSpace='pre-wrap'>
                                    {t('updating_rate_in_timeleft', {
                                        timeLeft: (
                                            <Text
                                                as='span'
                                                color='midnight70'
                                                font='label'
                                                fontSize='small'
                                                fontWeight='medium'
                                            >
                                                {dateTimeHelpers.formatTime(timer * 1000)}
                                            </Text>
                                        ),
                                    })}
                                </Text>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Text color='midnight70' font='label' fontSize='medium'>
                        {t('please_note_that_exchan_urrent_market_situation')}
                    </Text>

                    <Grid gridTemplateColumns='max-content max-content' gap={16} justifyContent='flex-end'>
                        <Button
                            color='midnight40'
                            variant='outline'
                            startIcon={<ChevronLeftIcon color='midnight100' />}
                            onClick={handleBack}
                            disabled={isLoadingCreateConversion}
                        >
                            {t('back')}
                        </Button>

                        <Button
                            disabled={isLoadingCreateConversionQuote}
                            color='blue80'
                            progress={isLoadingCreateConversion}
                            onClick={handleSubmit}
                        >
                            {t('confirm')}
                        </Button>
                    </Grid>
                </Grid>
            </Popup.Body>
        </>
    );
};

ConfirmConvertCurrencyPopupContent.displayName = 'ConfirmConvertCurrencyPopupContent';
